import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, MessageSquare, Calendar, Users, AlertCircle, Download, Trash2, Edit3, Filter, FileText, BarChart3 } from 'lucide-react'
import { announcementApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { showError } from '../utils/showError'
import { stripHtml } from '../utils/htmlHelpers'
import {
  PageHeader,
  SearchBar,
  ListContainer,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Button,
  Card,
  CardBody,
  FormSelect,
  FormField,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  StatusBadge,
  ConfirmationModal
} from '../components/common'

const Announcements = () => {
  const { selectedBuildingId } = useAuth()
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('') // Separate input state for typing
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState({
    priority: '',
    isDraft: '',
    isTemplate: '',
    isCommitted: '',
    fromDate: '',
    toDate: ''
  })
  const [selectedAnnouncements, setSelectedAnnouncements] = useState([])
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  const pageSize = 10

  const loadAnnouncements = async () => {
    try {
      setLoading(true)
      const params = {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
        ...(filters.priority && { priority: parseInt(filters.priority) }),
        ...(filters.isDraft !== '' && { isDraft: filters.isDraft === 'true' }),
        ...(selectedBuildingId && { buildingId: selectedBuildingId }),
        ...(filters.isTemplate !== '' && { isTemplate: filters.isTemplate === 'true' }),
        ...(filters.isCommitted !== '' && { isCommitted: filters.isCommitted === 'true' }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate })
      }

      const response = await announcementApi.list(params)
      console.log('Announcements API response:', response.data)

      setAnnouncements(response.data.items || [])
      setTotalPages(response.data.totalPages || 1)
      setTotalCount(response.data.totalCount || 0)
    } catch (error) {
      console.error('Error loading announcements:', error)
      setAnnouncements([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAnnouncements()
  }, [currentPage, searchTerm, filters, selectedBuildingId])

  useEffect(() => {
    setShowBulkActions(selectedAnnouncements.length > 0)
  }, [selectedAnnouncements])

  // Debounced search with proper cleanup
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
        setCurrentPage(1)
      }
    }, 500) // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId)
  }, [searchInput, searchTerm])

  const handleSearch = (term) => {
    setSearchInput(term) // Update input immediately for UI responsiveness
  }

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchTerm(searchInput) // Immediate search on Enter
      setCurrentPage(1)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAnnouncements(announcements.map(a => a.announcementId))
    } else {
      setSelectedAnnouncements([])
    }
  }

  const handleSelectAnnouncement = (announcementId, checked) => {
    if (checked) {
      setSelectedAnnouncements(prev => [...prev, announcementId])
    } else {
      setSelectedAnnouncements(prev => prev.filter(id => id !== announcementId))
    }
  }

  const handleBulkStatusUpdate = async (isDraft, isCommitted) => {
    try {
      await announcementApi.bulkUpdateStatus({
        announcementIds: selectedAnnouncements,
        isDraft,
        isCommitted
      })
      setSelectedAnnouncements([])
      setShowBulkActions(false)
      loadAnnouncements()
    } catch (error) {
      console.error('Error updating announcement status:', error)
      showError(error)
    }
  }

  const handleBulkDelete = async (forceDelete = false) => {
    if (!window.confirm(`Are you sure you want to delete ${selectedAnnouncements.length} announcements?`)) {
      return
    }

    try {
      await announcementApi.bulkDelete({
        announcementIds: selectedAnnouncements,
        forceDelete
      })
      setSelectedAnnouncements([])
      setShowBulkActions(false)
      loadAnnouncements()
    } catch (error) {
      console.error('Error deleting announcements:', error)
      showError(error)
    }
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const params = {
        ...(selectedBuildingId && { buildingId: selectedBuildingId }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate })
      }

      const response = await announcementApi.export(params)

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `announcements-${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting announcements:', error)
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  const clearFilters = () => {
    setFilters({
      priority: '',
      isCommitted: '',
      fromDate: '',
      toDate: ''
    })
    setSearchInput('')
    setSearchTerm('')
    setCurrentPage(1)
  }

  // Helper function to truncate text with word boundary
  const truncateText = (text, maxLength = 120) => {
    if (!text || text.length <= maxLength) return text

    const truncated = text.substr(0, maxLength)
    const lastSpace = truncated.lastIndexOf(' ')

    // Truncate at last word boundary if possible
    return lastSpace > maxLength * 0.8
      ? truncated.substr(0, lastSpace) + '...'
      : truncated + '...'
  }

  // Priority colors now handled by Badge component variants

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'High'
      case 2: return 'Medium'
      case 3: return 'Low'
      default: return 'Unknown'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Announcements"
        subtitle={`${totalCount} total announcements`}
        action={
          <div className="flex space-x-3">
            <Button
              onClick={handleExport}
              disabled={isExporting}
              variant="secondary"
              icon={<Download className="h-4 w-4" />}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              as={Link}
              to="/announcements/create"
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
            >
              Create Announcement
            </Button>
          </div>
        }
      />

      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <SearchBar
                value={searchInput}
                onChange={handleSearch}
                onKeyDown={handleSearchSubmit}
                placeholder="Search announcements... (Press Enter to search)"
              />
            </div>
            <div className="flex gap-2">
              <FormSelect
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                options={[
                  { value: '', label: 'All Priorities' },
                  { value: '1', label: 'High' },
                  { value: '2', label: 'Medium' },
                  { value: '3', label: 'Low' }
                ]}
                className="min-w-[120px]"
              />
              <FormSelect
                value={filters.isDraft}
                onChange={(e) => handleFilterChange('isDraft', e.target.value)}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'true', label: 'Draft' },
                  { value: 'false', label: 'Published' }
                ]}
                className="min-w-[120px]"
              />
              <Button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                variant="secondary"
                icon={<Filter className="h-4 w-4" />}
              >
                Filters
              </Button>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <FormSelect
                label="Committed Status"
                value={filters.isCommitted}
                onChange={(e) => handleFilterChange('isCommitted', e.target.value)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Committed' },
                  { value: 'false', label: 'Not Committed' }
                ]}
              />
              <FormField
                label="From Date"
                type="datetime-local"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
              <FormField
                label="To Date"
                type="datetime-local"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
              <div className="lg:col-span-4">
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="small"
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {showBulkActions && (
        <Card className="bg-primary-50 border-primary-200">
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-primary-900">
                  {selectedAnnouncements.length} announcement{selectedAnnouncements.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => handleBulkStatusUpdate(false, true)}
                  variant="accent"
                  icon={<Edit3 className="h-4 w-4" />}
                  size="small"
                >
                  Publish
                </Button>
                <Button
                  onClick={() => handleBulkStatusUpdate(true, false)}
                  variant="secondary"
                  icon={<Edit3 className="h-4 w-4" />}
                  size="small"
                >
                  Draft
                </Button>
                <Button
                  onClick={() => handleBulkDelete(false)}
                  variant="danger"
                  icon={<Trash2 className="h-4 w-4" />}
                  size="small"
                >
                  Delete
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <ListContainer>
        {announcements.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No announcements found"
            description="Get started by creating your first announcement."
            action={
              <Button
                as={Link}
                to="/announcements/create"
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
              >
                Create Announcement
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>
                    <input
                      type="checkbox"
                      checked={selectedAnnouncements.length === announcements.length && announcements.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </TableCell>
                  <TableCell header>Announcement</TableCell>
                  <TableCell header>Priority</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Recipients</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((announcement) => (
                  <TableRow key={announcement.announcementId || announcement.id} clickable>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedAnnouncements.includes(announcement.announcementId)}
                        onChange={(e) => handleSelectAnnouncement(announcement.announcementId, e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm font-semibold text-slate-900 flex items-center group-hover:text-primary-600 transition-colors">
                          {announcement.title}
                        </div>
                        <div className="text-sm text-slate-500 max-w-xs line-clamp-2 mt-1">
                          {truncateText(stripHtml(announcement.content), 100)}
                        </div>
                        {announcement.eventDate && (
                          <div className="text-xs text-slate-400 mt-1.5 font-medium">
                            Event: {formatDate(announcement.eventDate)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={announcement.priority === 1 ? 'danger' :
                          announcement.priority === 2 ? 'warning' : 'default'}
                      >
                        {getPriorityText(announcement.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={announcement.isCommitted ? "committed" : "not-committed"} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-slate-600 text-sm">
                        <Users className="w-4 h-4 mr-1.5 text-slate-400" />
                        {announcement.totalRecipients || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-slate-600 text-sm">
                        <Calendar className="w-4 h-4 mr-1.5 text-slate-400" />
                        {formatDate(announcement.createDateTimeUtc || announcement.createdDate)}
                      </div>
                      {announcement.scheduleDate && (
                        <div className="text-xs text-slate-400 mt-1.5 font-medium">
                          Scheduled: {formatDate(announcement.scheduleDate)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end pr-4">
                        <Button
                          as={Link}
                          to={`/announcements/${announcement.announcementId || announcement.id}`}
                          variant="ghost"
                          size="small"
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center px-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </ListContainer>
    </div>
  )
}

export default Announcements